import { useState, useEffect, useMemo, useCallback } from 'react'
import { activityService } from '../services/AdminServices'
import { adminApi } from '../services/ApiService'
import { useAuth } from '../context/AuthContext'
import {
  Calendar, Plus, Trash2, Save, X, ChevronLeft, ChevronRight,
  Clock, User, Users, Cpu, MapPin, Wrench, Phone, AlertTriangle, Check,
  FileText, Eye, Edit3, Search, Flag, ArrowRight
} from 'lucide-react'
import './CalendarHub.css'

const ACTIVITY_TYPES = [
  { value: 'visita', label: 'Visita', color: '#60a5fa', icon: MapPin },
  { value: 'instalacion', label: 'Instalación', color: '#4ade80', icon: Wrench },
  { value: 'mantenimiento', label: 'Mantenimiento', color: '#f59e0b', icon: Wrench },
  { value: 'consulta', label: 'Consulta', color: '#a78bfa', icon: Phone },
  { value: 'seguimiento', label: 'Seguimiento', color: '#38bdf8', icon: Eye },
  { value: 'soporte', label: 'Soporte', color: '#fb923c', icon: AlertTriangle },
  { value: 'otro', label: 'Otro', color: '#94a3b8', icon: FileText },
]

const ACTIVITY_STATUSES = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'completada', label: 'Completada' },
  { value: 'cancelada', label: 'Cancelada' },
]

const PRIORITIES = [
  { value: 'urgent', label: 'Urgente', color: '#ef4444', icon: '🔴' },
  { value: 'high', label: 'Alta', color: '#f59e0b', icon: '🟠' },
  { value: 'medium', label: 'Media', color: '#3b82f6', icon: '🔵' },
  { value: 'low', label: 'Baja', color: '#94a3b8', icon: '⚪' },
]

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function getTypeInfo(type) {
  return ACTIVITY_TYPES.find(t => t.value === type) || ACTIVITY_TYPES[ACTIVITY_TYPES.length - 1]
}

export default function CalendarHub() {
  const { user } = useAuth()
  const [view, setView] = useState('calendar') // calendar | week | diary | form
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [clients, setClients] = useState([])
  const [adminUsers, setAdminUsers] = useState([])
  const [clientDevices, setClientDevices] = useState([]) // devices for selected client
  const [selectedOwners, setSelectedOwners] = useState([]) // empty = all
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)

  // Form state
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({
    title: '', date: '', type: 'visita', clientId: '', clientName: '',
    description: '', duration: '', deviceMAC: '', status: 'pendiente',
    priority: 'medium', assignedTo: ''
  })

  // Filter
  const [typeFilter, setTypeFilter] = useState('all')

  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth()

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true)
      const month = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`
      const res = await activityService.list({ month })
      setActivities(res.activities || res.documents || [])
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [currentYear, currentMonth])

  useEffect(() => {
    loadActivities()
  }, [loadActivities])

  useEffect(() => {
    adminApi.get('/admin/clients').then(r => setClients(r.clients || r.documents || [])).catch(() => {})
    adminApi.get('/admin/admin-users').then(r => setAdminUsers(r.users || [])).catch(() => {})
  }, [])

  // Calendar grid computation
  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1)
    let startDay = firstDay.getDay() - 1 // Monday = 0
    if (startDay < 0) startDay = 6

    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
    const days = []

    // Previous month padding
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth, -i)
      days.push({ date: d, outside: true })
    }
    // Current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(currentYear, currentMonth, i), outside: false })
    }
    // Next month padding
    const remaining = 7 - (days.length % 7)
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        days.push({ date: new Date(currentYear, currentMonth + 1, i), outside: true })
      }
    }

    return days
  }, [currentYear, currentMonth])

  const allOwnersSelected = selectedOwners.length === 0 || (adminUsers.length > 0 && selectedOwners.length === adminUsers.length)
  const isMineOnly = selectedOwners.length === 1 && selectedOwners[0] === user?.email

  function toggleOwner(email) {
    setSelectedOwners(prev => {
      const current = prev.length === 0 ? adminUsers.map(u => u.email) : [...prev]
      return current.includes(email) ? current.filter(e => e !== email) : [...current, email]
    })
  }

  function toggleMine() {
    if (isMineOnly) {
      setSelectedOwners([])
    } else {
      setSelectedOwners([user?.email].filter(Boolean))
    }
  }

  function selectAllOwners() {
    setSelectedOwners([])
  }

  // Group activities by date key
  const activitiesByDate = useMemo(() => {
    const map = {}
    let filtered = activities
    if (selectedOwners.length > 0 && adminUsers.length > 0 && selectedOwners.length < adminUsers.length) {
      filtered = filtered.filter(a => selectedOwners.includes(a.assignedTo))
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      filtered = filtered.filter(a =>
        (a.title || '').toLowerCase().includes(q) ||
        (a.clientName || '').toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q)
      )
    }
    filtered.forEach(a => {
      const key = a.date?.substring(0, 10)
      if (key) {
        if (!map[key]) map[key] = []
        map[key].push(a)
      }
    })
    return map
  }, [activities, selectedOwners, adminUsers, user, searchTerm])

  // Selected day activities
  const selectedDayActivities = useMemo(() => {
    if (!selectedDate) return []
    const key = selectedDate.toISOString().substring(0, 10)
    const list = activitiesByDate[key] || []
    return list.sort((a, b) => (a.date || '').localeCompare(b.date || ''))
  }, [selectedDate, activitiesByDate])

  // Diary: all activities for current month, sorted
  const diaryActivities = useMemo(() => {
    let list = [...activities]
    if (selectedOwners.length > 0 && adminUsers.length > 0 && selectedOwners.length < adminUsers.length) {
      list = list.filter(a => selectedOwners.includes(a.assignedTo))
    }
    if (typeFilter !== 'all') list = list.filter(a => a.type === typeFilter)
    return list.sort((a, b) => (a.date || '').localeCompare(b.date || ''))
  }, [activities, typeFilter, selectedOwners, adminUsers, user])

  // Stats
  const stats = useMemo(() => {
    const total = activities.length
    const completadas = activities.filter(a => a.status === 'completada').length
    const pendientes = activities.filter(a => a.status === 'pendiente').length
    const todayStr = new Date().toISOString().substring(0, 10)
    const overdue = activities.filter(a => a.status === 'pendiente' && a.date?.substring(0, 10) < todayStr).length
    const byType = {}
    activities.forEach(a => {
      byType[a.type] = (byType[a.type] || 0) + 1
    })
    return { total, completadas, pendientes, overdue, byType }
  }, [activities])

  // Week view data
  const weekDays = useMemo(() => {
    const d = selectedDate || new Date()
    const dayOfWeek = d.getDay() === 0 ? 6 : d.getDay() - 1 // Monday = 0
    const monday = new Date(d)
    monday.setDate(d.getDate() - dayOfWeek)
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(monday)
      date.setDate(monday.getDate() + i)
      const key = date.toISOString().substring(0, 10)
      return { date, key, activities: activitiesByDate[key] || [] }
    })
  }, [selectedDate, activitiesByDate])

  function prevMonth() {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1))
    setSelectedDate(null)
  }

  function nextMonth() {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1))
    setSelectedDate(null)
  }

  function goToday() {
    const today = new Date()
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1))
    setSelectedDate(today)
  }

  function openForm(date, activity) {
    if (activity) {
      setEditingId(activity.$id)
      // Load devices for selected client
      const c = clients.find(c => (c.accountId || c.$id) === activity.clientId)
      setClientDevices(c?.devices || [])
      setForm({
        title: activity.title || '',
        date: activity.date?.substring(0, 10) || '',
        type: activity.type || 'visita',
        clientId: activity.clientId || '',
        clientName: activity.clientName || '',
        description: activity.description || '',
        duration: activity.duration || '',
        deviceMAC: activity.deviceMAC || '',
        status: activity.status || 'pendiente',
        priority: activity.priority || 'medium',
        assignedTo: activity.assignedTo || '',
      })
    } else {
      setEditingId(null)
      setClientDevices([])
      const dateStr = date ? date.toISOString().substring(0, 10) : new Date().toISOString().substring(0, 10)
      setForm({
        title: '', date: dateStr, type: 'visita', clientId: '', clientName: '',
        description: '', duration: '', deviceMAC: '', status: 'pendiente',
        priority: 'medium', assignedTo: ''
      })
    }
    setView('form')
  }

  async function saveActivity() {
    if (!form.title.trim() || !form.date) {
      setError('Título y fecha son requeridos')
      return
    }
    try {
      if (editingId) {
        await activityService.update(editingId, form)
      } else {
        await activityService.create(form)
      }
      setView('calendar')
      setEditingId(null)
      loadActivities()
    } catch (e) {
      setError(e.message)
    }
  }

  async function deleteActivity(id) {
    if (!confirm('¿Eliminar esta actividad?')) return
    try {
      await activityService.delete(id)
      loadActivities()
    } catch (e) {
      setError(e.message)
    }
  }

  async function toggleStatus(activity) {
    const newStatus = activity.status === 'completada' ? 'pendiente' : 'completada'
    try {
      await activityService.update(activity.$id, { status: newStatus })
      loadActivities()
    } catch (e) {
      setError(e.message)
    }
  }

  const isToday = (date) => {
    const today = new Date()
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
  }

  const isSelected = (date) => {
    if (!selectedDate) return false
    return date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
  }

  if (loading && activities.length === 0) {
    return <div className="cal-page"><div className="cal-loading"><div className="cal-spinner" /> Cargando actividades...</div></div>
  }

  return (
    <div className="cal-page">
      <div className="cal-header">
        <div className="cal-header-left">
          <Calendar size={28} />
          <div>
            <h1>Calendario de Actividades</h1>
            <p className="cal-subtitle">Agenda diaria — {MONTH_NAMES[currentMonth]} {currentYear}</p>
          </div>
        </div>
        <div className="cal-header-actions">
          <button
            className={`cal-btn ${isMineOnly ? 'cal-btn-primary' : 'cal-btn-secondary'}`}
            onClick={toggleMine}
          >
            <User size={16} /> Mis actividades
          </button>
          {adminUsers.length > 0 && (
            <div className="owner-multiselect">
              <button className="owner-multiselect-trigger" onClick={() => setShowOwnerDropdown(v => !v)}>
                <Users size={14} />
                {allOwnersSelected ? 'Todo el equipo' : `${selectedOwners.length} seleccionado${selectedOwners.length !== 1 ? 's' : ''}`}
              </button>
              {showOwnerDropdown && (
                <div className="owner-multiselect-dropdown">
                  <div className="owner-multiselect-item" onClick={selectAllOwners}>
                    <input type="checkbox" checked={allOwnersSelected} readOnly />
                    <span>👥 Todo el equipo</span>
                  </div>
                  {adminUsers.map(u => {
                    const emails = selectedOwners.length === 0 ? adminUsers.map(x => x.email) : selectedOwners
                    const checked = emails.includes(u.email)
                    return (
                      <div key={u.$id} className="owner-multiselect-item" onClick={() => toggleOwner(u.email)}>
                        <input type="checkbox" checked={checked} readOnly />
                        <span>{u.name || u.email}</span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
          <button className="cal-btn cal-btn-secondary" onClick={goToday}>Hoy</button>
          <button className="cal-btn cal-btn-primary" onClick={() => openForm(selectedDate || new Date())}>
            <Plus size={16} /> Nueva Actividad
          </button>
        </div>
      </div>

      {error && <div className="cal-error"><AlertTriangle size={16} /> {error} <button onClick={() => setError(null)}><X size={14} /></button></div>}

      {/* Stats */}
      <div className="cal-stats">
        <div className="cal-stat"><span className="cal-stat-num">{stats.total}</span><span className="cal-stat-label">Total</span></div>
        <div className="cal-stat"><span className="cal-stat-num" style={{ color: '#4ade80' }}>{stats.completadas}</span><span className="cal-stat-label">Completadas</span></div>
        <div className="cal-stat"><span className="cal-stat-num" style={{ color: '#f59e0b' }}>{stats.pendientes}</span><span className="cal-stat-label">Pendientes</span></div>
        {stats.overdue > 0 && (
          <div className="cal-stat cal-stat-alert"><span className="cal-stat-num" style={{ color: '#f87171' }}>{stats.overdue}</span><span className="cal-stat-label">⚠️ Vencidas</span></div>
        )}
        {ACTIVITY_TYPES.slice(0, 4).map(t => (
          <div className="cal-stat" key={t.value}>
            <span className="cal-stat-num" style={{ color: t.color }}>{stats.byType[t.value] || 0}</span>
            <span className="cal-stat-label">{t.label}</span>
          </div>
        ))}
      </div>

      {/* Toolbar: View toggle + Search */}
      <div className="cal-toolbar">
        <div className="cal-view-toggle">
          <button className={view === 'calendar' ? 'active' : ''} onClick={() => setView('calendar')}>
            <Calendar size={16} /> Mes
          </button>
          <button className={view === 'week' ? 'active' : ''} onClick={() => { setView('week'); if (!selectedDate) setSelectedDate(new Date()) }}>
            <Calendar size={16} /> Semana
          </button>
          <button className={view === 'diary' ? 'active' : ''} onClick={() => setView('diary')}>
            <FileText size={16} /> Diario
          </button>
        </div>
        <div className="cal-search"><Search size={14} /><input placeholder="Buscar actividad..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} /></div>
      </div>

      {/* ───── CALENDAR VIEW ───── */}
      {view === 'calendar' && (
        <div className="cal-grid-container">
          <div className="cal-nav">
            <button onClick={prevMonth}><ChevronLeft size={20} /></button>
            <h2>{MONTH_NAMES[currentMonth]} {currentYear}</h2>
            <button onClick={nextMonth}><ChevronRight size={20} /></button>
          </div>

          <div className="cal-grid">
            {DAY_NAMES.map(d => <div key={d} className="cal-day-header">{d}</div>)}
            {calendarDays.map((day, i) => {
              const key = day.date.toISOString().substring(0, 10)
              const dayActivities = activitiesByDate[key] || []
              return (
                <div
                  key={i}
                  className={`cal-day ${day.outside ? 'outside' : ''} ${isToday(day.date) ? 'today' : ''} ${isSelected(day.date) ? 'selected' : ''}`}
                  onClick={() => { setSelectedDate(day.date) }}
                  onDoubleClick={() => openForm(day.date)}
                >
                  <span className="cal-day-num">{day.date.getDate()}</span>
                  {dayActivities.length > 0 && (
                    <div className="cal-day-dots">
                      {dayActivities.slice(0, 3).map((a, j) => (
                        <span key={j} className="cal-dot" style={{ background: getTypeInfo(a.type).color }} title={a.title} />
                      ))}
                      {dayActivities.length > 3 && <span className="cal-dot-more">+{dayActivities.length - 3}</span>}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Selected day detail */}
          {selectedDate && (
            <div className="cal-day-detail">
              <div className="cal-day-detail-header">
                <h3>{selectedDate.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
                <button className="cal-btn cal-btn-sm" onClick={() => openForm(selectedDate)}>
                  <Plus size={14} /> Agregar
                </button>
              </div>
              {selectedDayActivities.length === 0 ? (
                <p className="cal-empty">Sin actividades para este día</p>
              ) : (
                <div className="cal-day-activities">
                  {selectedDayActivities.map(a => {
                    const typeInfo = getTypeInfo(a.type)
                    const TypeIcon = typeInfo.icon
                    const prioInfo = PRIORITIES.find(p => p.value === a.priority)
                    const todayStr = new Date().toISOString().substring(0, 10)
                    const isOverdue = a.status === 'pendiente' && a.date?.substring(0, 10) < todayStr
                    return (
                      <div key={a.$id} className={`cal-activity-card ${a.status === 'completada' ? 'done' : ''} ${isOverdue ? 'overdue' : ''}`}>
                        <div className="cal-activity-left">
                          <button className="cal-check" onClick={() => toggleStatus(a)}
                            style={{ borderColor: typeInfo.color, background: a.status === 'completada' ? typeInfo.color : 'transparent' }}>
                            {a.status === 'completada' && <Check size={12} />}
                          </button>
                          <div>
                            <div className="cal-activity-title-row">
                              {prioInfo && <span className="cal-activity-prio" title={prioInfo.label}>{prioInfo.icon}</span>}
                              <span className="cal-activity-title">{a.title}</span>
                              {isOverdue && <span className="cal-overdue-badge">Vencida</span>}
                            </div>
                            <div className="cal-activity-meta">
                              <span className="cal-type-badge" style={{ color: typeInfo.color, borderColor: typeInfo.color }}>
                                <TypeIcon size={12} /> {typeInfo.label}
                              </span>
                              {a.clientName && <span><User size={12} /> {a.clientName}</span>}
                              {a.duration && <span><Clock size={12} /> {a.duration}</span>}
                              {a.deviceMAC && <span><Cpu size={12} /> {a.deviceMAC}</span>}
                              {a.assignedTo && <span style={{ color: '#c4b5fd' }}><User size={12} /> {a.assignedTo}</span>}
                            </div>
                            {a.description && <p className="cal-activity-desc">{a.description}</p>}
                          </div>
                        </div>
                        <div className="cal-activity-actions">
                          <button onClick={() => openForm(null, a)} title="Editar"><Edit3 size={14} /></button>
                          <button onClick={() => deleteActivity(a.$id)} title="Eliminar"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ───── WEEK VIEW ───── */}
      {view === 'week' && (
        <div className="cal-week-container">
          <div className="cal-nav">
            <button onClick={() => {
              const d = new Date(selectedDate || new Date())
              d.setDate(d.getDate() - 7)
              setSelectedDate(d)
            }}><ChevronLeft size={20} /></button>
            <h2>Semana del {weekDays[0].date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })} al {weekDays[6].date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })}</h2>
            <button onClick={() => {
              const d = new Date(selectedDate || new Date())
              d.setDate(d.getDate() + 7)
              setSelectedDate(d)
            }}><ChevronRight size={20} /></button>
          </div>
          <div className="cal-week-grid">
            {weekDays.map(wd => {
              const todayStr = new Date().toISOString().substring(0, 10)
              const isToday = wd.key === todayStr
              return (
                <div key={wd.key} className={`cal-week-day ${isToday ? 'today' : ''}`}>
                  <div className="cal-week-day-header">
                    <span className="cal-week-day-name">{DAY_NAMES[wd.date.getDay() === 0 ? 6 : wd.date.getDay() - 1]}</span>
                    <span className={`cal-week-day-num ${isToday ? 'today' : ''}`}>{wd.date.getDate()}</span>
                    <button className="cal-week-add" onClick={() => openForm(wd.date)}><Plus size={12} /></button>
                  </div>
                  <div className="cal-week-day-body">
                    {wd.activities.length === 0 && <span className="cal-week-empty">—</span>}
                    {wd.activities.map(a => {
                      const typeInfo = getTypeInfo(a.type)
                      const prioInfo = PRIORITIES.find(p => p.value === a.priority)
                      const isOverdue = a.status === 'pendiente' && a.date?.substring(0, 10) < todayStr
                      return (
                        <div key={a.$id}
                          className={`cal-week-activity ${a.status === 'completada' ? 'done' : ''} ${isOverdue ? 'overdue' : ''}`}
                          style={{ borderLeftColor: typeInfo.color }}
                          onClick={() => openForm(null, a)}>
                          <div className="cal-week-act-top">
                            {prioInfo && <span className="cal-week-prio">{prioInfo.icon}</span>}
                            <span className="cal-week-act-title">{a.title}</span>
                          </div>
                          <div className="cal-week-act-meta">
                            <span style={{ color: typeInfo.color }}>{typeInfo.label}</span>
                            {a.clientName && <span><User size={10} /> {a.clientName}</span>}
                            {a.duration && <span><Clock size={10} /> {a.duration}</span>}
                          </div>
                          <div className="cal-week-act-bottom">
                            <button className="cal-check-sm" onClick={e => { e.stopPropagation(); toggleStatus(a) }}
                              style={{ borderColor: typeInfo.color, background: a.status === 'completada' ? typeInfo.color : 'transparent' }}>
                              {a.status === 'completada' && <Check size={9} />}
                            </button>
                            {a.assignedTo && <span className="cal-week-owner">{a.assignedTo.split('@')[0]}</span>}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ───── DIARY VIEW ───── */}
      {view === 'diary' && (
        <div className="cal-diary">
          <div className="cal-diary-filters">
            <div className="cal-nav">
              <button onClick={prevMonth}><ChevronLeft size={20} /></button>
              <h2>{MONTH_NAMES[currentMonth]} {currentYear}</h2>
              <button onClick={nextMonth}><ChevronRight size={20} /></button>
            </div>
            <div className="cal-type-filters">
              <button className={typeFilter === 'all' ? 'active' : ''} onClick={() => setTypeFilter('all')}>Todas</button>
              {ACTIVITY_TYPES.map(t => (
                <button key={t.value} className={typeFilter === t.value ? 'active' : ''} onClick={() => setTypeFilter(t.value)}
                  style={typeFilter === t.value ? { borderColor: t.color, color: t.color } : {}}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {diaryActivities.length === 0 ? (
            <div className="cal-empty-state">
              <Calendar size={48} />
              <p>No hay actividades en {MONTH_NAMES[currentMonth]}</p>
              <button className="cal-btn cal-btn-primary" onClick={() => openForm()}>
                <Plus size={16} /> Crear primera actividad
              </button>
            </div>
          ) : (
            <div className="cal-diary-list">
              {diaryActivities.map((a, i) => {
                const typeInfo = getTypeInfo(a.type)
                const TypeIcon = typeInfo.icon
                const showDateHeader = i === 0 || a.date?.substring(0, 10) !== diaryActivities[i - 1]?.date?.substring(0, 10)
                return (
                  <div key={a.$id}>
                    {showDateHeader && (
                      <div className="cal-diary-date-header">
                        {new Date(a.date).toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </div>
                    )}
                    <div className={`cal-diary-entry ${a.status === 'completada' ? 'done' : ''}`}>
                      <div className="cal-diary-time-col" style={{ borderColor: typeInfo.color }}>
                        <TypeIcon size={16} style={{ color: typeInfo.color }} />
                      </div>
                      <div className="cal-diary-content">
                        <div className="cal-diary-header-row">
                          <span className="cal-diary-title">{a.title}</span>
                          <span className="cal-type-badge" style={{ color: typeInfo.color, borderColor: typeInfo.color }}>
                            {typeInfo.label}
                          </span>
                        </div>
                        {a.description && <p className="cal-diary-desc">{a.description}</p>}
                        <div className="cal-diary-meta">
                          {a.clientName && <span><User size={12} /> {a.clientName}</span>}
                          {a.duration && <span><Clock size={12} /> {a.duration}</span>}
                          {a.deviceMAC && <span><Cpu size={12} /> {a.deviceMAC}</span>}
                          {a.assignedTo && <span style={{ color: '#c4b5fd' }}><User size={12} /> {a.assignedTo}</span>}
                          <span className={`cal-status-pill ${a.status}`}>{a.status}</span>
                        </div>
                      </div>
                      <div className="cal-diary-actions">
                        <button onClick={() => toggleStatus(a)} title={a.status === 'completada' ? 'Marcar pendiente' : 'Completar'}>
                          <Check size={14} />
                        </button>
                        <button onClick={() => openForm(null, a)} title="Editar"><Edit3 size={14} /></button>
                        <button onClick={() => deleteActivity(a.$id)} title="Eliminar"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ───── FORM (Create/Edit) ───── */}
      {view === 'form' && (
        <div className="cal-form-overlay">
          <div className="cal-form">
            <div className="cal-form-header">
              <h2>{editingId ? 'Editar Actividad' : 'Nueva Actividad'}</h2>
              <button onClick={() => setView('calendar')}><X size={20} /></button>
            </div>

            <div className="cal-form-body">
              <div className="cal-form-row">
                <label>Título *</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="Ej: Instalación GP-BOX en sala 2" />
              </div>

              <div className="cal-form-row-2col">
                <div className="cal-form-row">
                  <label>Fecha *</label>
                  <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
                </div>
                <div className="cal-form-row">
                  <label>Duración</label>
                  <input value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })}
                    placeholder="Ej: 2hs, 30min" />
                </div>
              </div>

              <div className="cal-form-row-2col">
                <div className="cal-form-row">
                  <label>Tipo</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                    {ACTIVITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="cal-form-row">
                  <label>Estado</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    {ACTIVITY_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="cal-form-row-2col">
                <div className="cal-form-row">
                  <label>Prioridad</label>
                  <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.icon} {p.label}</option>)}
                  </select>
                </div>
                <div className="cal-form-row">
                  <label>Asignar a</label>
                  <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}>
                    <option value="">— Sin asignar —</option>
                    {adminUsers.map(u => <option key={u.$id} value={u.email}>{u.name || u.email}</option>)}
                  </select>
                </div>
              </div>

              <div className="cal-form-row">
                <label>Cliente</label>
                <select value={form.clientId} onChange={e => {
                  const c = clients.find(c => (c.accountId || c.$id) === e.target.value)
                  const devs = c?.devices || []
                  setClientDevices(devs)
                  setForm({ ...form, clientId: e.target.value, clientName: c?.name || '', deviceMAC: '' })
                }}>
                  <option value="">— Sin cliente —</option>
                  {clients.map(c => {
                    const id = c.accountId || c.$id
                    return <option key={id} value={id}>{c.name} ({c.email})</option>
                  })}
                </select>
              </div>

              <div className="cal-form-row">
                <label>Dispositivo (MAC)</label>
                {clientDevices.length > 0 ? (
                  <select value={form.deviceMAC} onChange={e => setForm({ ...form, deviceMAC: e.target.value })}>
                    <option value="">— Sin dispositivo —</option>
                    {clientDevices.map(d => (
                      <option key={d.mac} value={d.mac}>
                        {d.mac} {d.name ? `(${d.name})` : ''} {d.model ? `[${d.model}]` : ''} {d.isActive ? '🟢' : '🔴'}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input value={form.deviceMAC} onChange={e => setForm({ ...form, deviceMAC: e.target.value })}
                    placeholder={form.clientId ? 'Cliente sin dispositivos' : 'Seleccione un cliente primero'} />
                )}
              </div>

              <div className="cal-form-row">
                <label>Descripción / Notas</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Detalles de la actividad..." rows={4} />
              </div>
            </div>

            <div className="cal-form-footer">
              <button className="cal-btn cal-btn-secondary" onClick={() => setView('calendar')}>
                <X size={16} /> Cancelar
              </button>
              <button className="cal-btn cal-btn-primary" onClick={saveActivity}>
                <Save size={16} /> {editingId ? 'Guardar Cambios' : 'Crear Actividad'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
